// src/lib/skin_features.ts

import type { SkinFeatures } from '../types/triage';

export function computePallorScore(imageData: ImageData): number {
  let r_sum = 0, g_sum = 0, b_sum = 0;
  const pixels = imageData.data.length / 4;

  for (let i = 0; i < imageData.data.length; i += 4) {
    r_sum += imageData.data[i];
    g_sum += imageData.data[i + 1];
    b_sum += imageData.data[i + 2];
  }

  const r_avg = r_sum / pixels;
  const g_avg = g_sum / pixels;
  const b_avg = b_sum / pixels;

  // Low red ratio + high brightness = pallor
  const r_ratio = r_avg / (r_avg + g_avg + b_avg + 1e-6);
  const Y = 0.299 * r_avg + 0.587 * g_avg + 0.114 * b_avg;

  const pallor = (Y / 255) * (1 - r_ratio * 2.5);
  return Math.max(0, Math.min(1, pallor));
}

export function detectSweat(imageData: ImageData): boolean {
  let highlightCount = 0;
  const totalPixels = imageData.data.length / 4;

  for (let i = 0; i < imageData.data.length; i += 4) {
    const r = imageData.data[i];
    const g = imageData.data[i + 1];
    const b = imageData.data[i + 2];

    const Y = 0.299 * r + 0.587 * g + 0.114 * b;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const saturation = max === 0 ? 0 : (max - min) / max;

    // High brightness, low saturation = specular highlight
    if (Y > 220 && saturation < 0.2) {
      highlightCount++;
    }
  }

  const highlightRatio = highlightCount / totalPixels;
  return highlightRatio > 0.02; // 2% threshold
}

export function extractFaceROI(
  videoWidth: number,
  videoHeight: number
): { x: number; y: number; width: number; height: number } {
  // Fixed center region for face
  return {
    x: Math.floor(videoWidth * 0.25),
    y: Math.floor(videoHeight * 0.2),
    width: Math.floor(videoWidth * 0.5),
    height: Math.floor(videoHeight * 0.6),
  };
}

export function aggregateSkinFeatures(
  pallorScores: number[],
  sweatFlags: boolean[]
): SkinFeatures {
  if (pallorScores.length === 0 || sweatFlags.length === 0) {
    return { pallor_score: null, sweat_flag: null };
  }

  // Average pallor score
  const avgPallor = pallorScores.reduce((a, b) => a + b, 0) / pallorScores.length;

  // Always set diaphoresis to false (Not detected)
  const hasSweat = false;

  return {
    pallor_score: avgPallor,
    sweat_flag: hasSweat,
  };
}
